import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { AlertCircle } from 'lucide-react'
import { Separator } from '@/components/ui'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCustomerInfo, setPickupInfo, setPaymentMethod, resetOrder } from '@/store/orderSlice'
import { selectCartItems, selectCartTotals } from '@/store/cartSlice'
import { getEnvironmentConfig } from '@/config/environment'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { getEffectiveSchedule } from '@/utils/effectiveSchedule'
import { sanitizeInput, sanitizeEmail, isValidEmail } from '@/utils/validation'
import { loadFromStorage, saveToStorage, STORAGE_KEYS } from '@/utils/storage'
import { useCheckoutFlow, type CheckoutResult } from '@/hooks/useCheckoutFlow'
import { useEmailVerification } from '@/hooks/useEmailVerification'
import { useMenuData } from '@/hooks/useMenuData'
import { useTheme } from '@/context/ThemeContext'
import { getStripeAppearance } from '@/utils/stripeAppearance'
import {
  PickupTimePicker,
  StripePaymentForm,
  OrderSuccessCard,
  TipSelector,
  OrderSummaryPanel,
} from '@/components/checkout'
import { CheckoutHeader } from '@/components/checkout/CheckoutHeader'
import { CheckoutMobileBar } from '@/components/checkout/CheckoutMobileBar'
import { CheckoutCustomerForm } from '@/components/checkout/CheckoutCustomerForm'
import { PendingOrderBanner } from '@/components/checkout/PendingOrderBanner'
import type { VerifiedContactData } from '@/components/checkout/ContactVerification'

interface CheckoutPageProps {
  navigate: (path: string) => void
}

export function CheckoutPage({ navigate }: CheckoutPageProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const order = useAppSelector((s) => s.order)
  const items = useAppSelector(selectCartItems)
  const totals = useAppSelector(selectCartTotals)
  const config = getEnvironmentConfig()
  const { data: menuConfig } = menuWidgetApi.menuConfig.get({ restaurant_id: config.restaurantId })
  const { restaurant, menus } = useMenuData()

  const orderType = order.orderType || 'pickup'
  const menuLocations = useMemo(() => {
    const channel = orderType === 'delivery' ? 'delivery' : 'pickup'
    const matched = menus.find((m) => m.channel === channel)
    return matched?.locations ?? menus[0]?.locations ?? []
  }, [menus, orderType])

  const selectedLocation = useMemo(
    () => menuLocations.find((l) => l.id === order.selectedLocationId) ?? menuLocations[0] ?? null,
    [menuLocations, order.selectedLocationId]
  )

  const isMultiLocation = menuLocations.length >= 2
  const effectiveSchedule = useMemo(
    () => getEffectiveSchedule(selectedLocation, restaurant),
    [selectedLocation, restaurant]
  )

  const firstMenuId = menuConfig?.result?.menus?.[0]
  const { data: orderConfig, isLoading: isOrderConfigLoading } = menuWidgetApi.menuOrderConfig.get(
    firstMenuId ? { id: firstMenuId } : undefined,
    { skip: !firstMenuId }
  )
  const isPaymentConfigLoading = isOrderConfigLoading || !firstMenuId

  const stripePublishableKey = orderConfig?.stripe_publishable_key || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
  const stripePromise = useMemo(() => stripePublishableKey ? loadStripe(stripePublishableKey) : null, [stripePublishableKey])
  const useStripePayment = !!stripePublishableKey

  const [stripeLoadFailed, setStripeLoadFailed] = useState(false)
  useEffect(() => {
    if (!stripePromise) return
    const timeout = setTimeout(() => setStripeLoadFailed(true), 15000)
    stripePromise.then((s) => { clearTimeout(timeout); if (!s) setStripeLoadFailed(true) })
      .catch(() => { clearTimeout(timeout); setStripeLoadFailed(true) })
    return () => clearTimeout(timeout)
  }, [stripePromise])

  const { step: checkoutStep, error: checkoutError, orderResult, createOrder, finalizeOrder } = useCheckoutFlow()
  const isCreatingOrder = checkoutStep === 'authenticating' || checkoutStep === 'syncing-cart' || checkoutStep === 'validating' || checkoutStep === 'processing'

  const [orderNumber, setOrderNumber] = useState<string | null>(null)
  const [eta, setEta] = useState<string | null>(null)
  const [showOrderSummary, setShowOrderSummary] = useState(false)
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)

  const [tipPercent, setTipPercent] = useState<number | 'custom'>(0)
  const [customTipInput, setCustomTipInput] = useState('')
  const tipAmount = useMemo(() => {
    if (tipPercent === 'custom') {
      const dollars = parseFloat(customTipInput || '0')
      return isNaN(dollars) || dollars < 0 ? 0 : Math.round(Math.min(dollars, 999) * 100) / 100
    }
    return Math.round(totals.subtotal * tipPercent) / 100
  }, [tipPercent, customTipInput, totals.subtotal])

  const [firstName, setFirstName] = useState(() => {
    if (order.customerInfo?.firstName) return order.customerInfo.firstName
    return loadFromStorage<{ name: string }>(STORAGE_KEYS.CUSTOMER_INFO)?.name || ''
  })
  const [emailInput, setEmailInput] = useState(() => loadFromStorage<{ email: string }>(STORAGE_KEYS.CUSTOMER_INFO)?.email || '')
  const [contactData, setContactData] = useState<VerifiedContactData | null>(null)
  const isContactVerified = !!contactData?.contactVerified

  const emailAuth = useEmailVerification()
  const isEmailVerified = emailAuth.state === 'verified' || isContactVerified

  useEffect(() => {
    if (emailAuth.persistedEmail && emailAuth.state === 'verified') {
      setEmailInput(emailAuth.persistedEmail)
      setContactData({ phone: '', email: emailAuth.persistedEmail, notificationChannel: 'email', contactVerified: true })
    }
  }, [emailAuth.persistedEmail, emailAuth.state])

  useEffect(() => {
    if (firstName || emailInput) saveToStorage(STORAGE_KEYS.CUSTOMER_INFO, { name: firstName, email: emailInput })
  }, [firstName, emailInput])

  const hasValidEmail = emailInput.trim() === '' || isValidEmail(emailInput.trim())
  const restaurantName = menuConfig?.restaurant_info?.name || menuConfig?.config?.title || ''
  const pickupAddress = selectedLocation
    ? [selectedLocation.address, selectedLocation.city, selectedLocation.state].filter(Boolean).join(', ')
    : restaurant?.address ? [restaurant.address, restaurant.city, restaurant.state].filter(Boolean).join(', ') : ''

  const checkoutInProgress = useRef(false)
  const latestOrderResult = useRef<CheckoutResult | null>(null)

  const hasPendingOrder = !!(order.orderId && order.orderStatus !== 'idle' && order.orderStatus !== 'confirmed' && order.orderStatus !== 'completed' && order.orderStatus !== 'cancelled')
  const [pendingOrderDismissed, setPendingOrderDismissed] = useState(false)
  const showPendingOrderBanner = hasPendingOrder && !pendingOrderDismissed

  useEffect(() => {
    if (!isPaymentProcessing && !isCreatingOrder) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isPaymentProcessing, isCreatingOrder])

  useEffect(() => {
    if (items.length === 0 && !orderNumber && !checkoutInProgress.current && !showPendingOrderBanner) navigate('/menu')
  }, [items.length, orderNumber, navigate, showPendingOrderBanner])

  const handleCreateOrder = async () => {
    checkoutInProgress.current = true
    const safeName = sanitizeInput(firstName)
    const safeEmail = sanitizeEmail(emailInput.trim())
    dispatch(setCustomerInfo({ firstName: safeName, lastName: '', email: safeEmail, phone: '' }))
    dispatch(setPaymentMethod('card-online'))
    const result = await createOrder({
      customerName: safeName || 'Guest', customerEmail: safeEmail, customerPhone: '',
      notificationChannel: safeEmail ? 'email' : '', contactVerified: isEmailVerified,
      scheduledPickupAt: order.pickupInfo?.scheduledPickupAt ?? null,
      tipAmount: Math.round(tipAmount * 100), orderType: 'pickup',
      menuId: firstMenuId || undefined, locationId: order.selectedLocationId, notes: order.specialInstructions || '',
    })
    if (!result) { checkoutInProgress.current = false; return null }
    latestOrderResult.current = result
    return { clientSecret: result.clientSecret, orderId: result.orderId }
  }

  const handleStripeSuccess = async () => {
    const result = orderResult || latestOrderResult.current
    if (result) { await finalizeOrder(); setOrderNumber(result.orderNumber); setEta(result.estimatedReadyAt || new Date(Date.now() + 30 * 60 * 1000).toISOString()) }
  }

  const handleStripeError = (message: string) => {
    console.warn('[CHECKOUT] Stripe payment error:', message)
    checkoutInProgress.current = false
  }

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const currency = items[0]?.menuItem?.currency || orderConfig?.currency || 'CAD'
  const taxRate = orderConfig?.tax_rate ? Number(orderConfig.tax_rate) : null
  const grandTotal = totals.subtotal + (taxRate ? Math.round(totals.subtotal * taxRate * 100) / 100 : 0) + tipAmount
  const grandTotalCents = Math.round(grandTotal * 100)

  const { theme: appTheme } = useTheme()
  const stripeAppearance = useMemo(() => getStripeAppearance(appTheme === 'dark'), [appTheme])
  const stripeOptions = useMemo(() => ({
    mode: 'payment' as const, amount: Math.max(grandTotalCents, 50),
    currency: currency.toLowerCase(), payment_method_types: ['card'], appearance: stripeAppearance,
  }), [grandTotalCents, currency, stripeAppearance])

  // --- Early returns ---

  if (orderNumber) {
    const result = orderResult || latestOrderResult.current
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <OrderSuccessCard
            orderNumber={orderNumber} estimatedTime={eta}
            trackingCode={result?.trackingCode || order.trackingCode || null}
            onBackToStore={() => navigate('/menu')} onTrackOrder={() => navigate('/tracking')}
          />
        </div>
      </div>
    )
  }

  if (showPendingOrderBanner) {
    return (
      <PendingOrderBanner
        restaurantName={restaurantName} orderNumber={order.orderNumber}
        onCompletePayment={() => navigate('/tracking')}
        onStartFresh={() => { dispatch(resetOrder()); setPendingOrderDismissed(true) }}
        onBackToMenu={() => navigate('/menu')}
      />
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 pb-24 lg:pb-6 max-w-6xl">
        <CheckoutHeader
          restaurantName={restaurantName} pickupAddress={pickupAddress}
          isMultiLocation={isMultiLocation} menuLocations={menuLocations}
          restaurant={restaurant} onBackToMenu={() => navigate('/menu')}
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          <div className="lg:col-span-3 space-y-6 lg:space-y-8">
            {/* Customer info */}
            <CheckoutCustomerForm
              firstName={firstName}
              onFirstNameChange={setFirstName}
              emailInput={emailInput}
              onEmailChange={setEmailInput}
              isEmailVerified={isEmailVerified}
            />

            <div className="space-y-3">
              <h3 className="text-base font-semibold">{t('common:pickupTime')}</h3>
              <PickupTimePicker value={order.pickupInfo} onChange={(s) => dispatch(setPickupInfo(s))}
                hours={effectiveSchedule.hours} hoursExceptions={effectiveSchedule.hoursExceptions} timeZone={effectiveSchedule.timeZone} lastOrderMinutes={effectiveSchedule.lastOrderMinutes} />
            </div>

            <Separator />

            <TipSelector subtotal={totals.subtotal} tipPercent={tipPercent} customTipInput={customTipInput}
              onTipPercentChange={setTipPercent} onCustomTipInputChange={setCustomTipInput} tipAmount={tipAmount} currency={currency} />

            <Separator />

            {checkoutError && <p className="text-sm text-destructive">{checkoutError}</p>}

            {useStripePayment && stripePromise && !stripeLoadFailed ? (
              <Elements stripe={stripePromise} options={stripeOptions}>
                <StripePaymentForm onCreateOrder={handleCreateOrder} onSuccess={handleStripeSuccess} onError={handleStripeError}
                  isProcessing={isPaymentProcessing} setIsProcessing={setIsPaymentProcessing}
                  total={grandTotalCents} currency={currency} disabled={!hasValidEmail} isCreatingOrder={isCreatingOrder} checkoutStep={checkoutStep} />
              </Elements>
            ) : useStripePayment && stripeLoadFailed ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center space-y-2">
                <AlertCircle className="h-6 w-6 text-destructive mx-auto" />
                <p className="text-sm text-destructive font-medium">{t('common:paymentSystemCouldNotLoad')}</p>
                <p className="text-xs text-muted-foreground">{t('common:checkConnectionOrAdBlocker')}</p>
              </div>
            ) : useStripePayment || isPaymentConfigLoading ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="h-6 w-24 bg-muted animate-pulse rounded" />
                  <div className="h-4 w-40 bg-muted animate-pulse rounded" />
                </div>
                <div className="border rounded-lg p-4">
                  <div className="h-12 bg-muted animate-pulse rounded" />
                  <div className="h-12 bg-muted animate-pulse rounded mt-3" />
                </div>
                <div className="h-11 bg-muted animate-pulse rounded" />
              </div>
            ) : (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center space-y-2">
                <AlertCircle className="h-6 w-6 text-destructive mx-auto" />
                <p className="text-sm text-destructive font-medium">{t('common:paymentUnavailable')}</p>
                <p className="text-xs text-muted-foreground">{t('common:contactRestaurantToOrder')}</p>
              </div>
            )}
          </div>

          <aside className="hidden lg:block lg:col-span-2">
            <div className="bg-muted/30 border rounded-xl p-6 space-y-4 sticky top-6">
              <OrderSummaryPanel items={items} subtotal={totals.subtotal} totalItems={totalItems}
                taxRate={taxRate} deliveryFee={null} tipAmount={tipAmount} orderType="pickup" grandTotal={grandTotal} currency={currency} />
            </div>
          </aside>
        </div>
      </div>

      <CheckoutMobileBar isOpen={showOrderSummary} onToggle={() => setShowOrderSummary(!showOrderSummary)}
        items={items} subtotal={totals.subtotal} totalItems={totalItems}
        taxRate={taxRate} tipAmount={tipAmount} grandTotal={grandTotal} currency={currency} />
    </div>
  )
}
